// src/components/blog/BlogHome.js
import React, { useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BlogContext } from '../../contexts/BlogContext';
import { format } from 'date-fns';
import { ArrowRight } from 'lucide-react';
import BlogPostCard from './BlogPostCard';
import LoadingSpinner from './LoadingSpinner';

function BlogHome() {
  const { 
    posts, 
    featuredPost, 
    loading, 
    error, 
    hasMore, 
    fetchMorePosts 
  } = useContext(BlogContext);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* SEO Metadata */}
      <Helmet>
        <title>ReelIntro Blog: Video Introduction Tips & Career Advice</title>
        <meta name="description" content="Explore expert tips for creating impressive video introductions and discover career advice to help you stand out in your job search." />
        <meta property="og:title" content="ReelIntro Blog: Video Introduction Tips & Career Advice" />
        <meta property="og:description" content="Explore expert tips for creating impressive video introductions and discover career advice to help you stand out in your job search." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://reelintro.com/blog" />
        <meta property="og:image" content="https://reelintro.com/logo512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "ReelIntro Blog",
            "description": "Video introduction tips and career advice to help you stand out in your job search.",
            "url": "https://reelintro.com/blog"
          }
        `}</script>
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        <header className="mb-12 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">ReelIntro Blog</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Expert tips for creating impressive video introductions and career advice to help you stand out in your job search
          </p>
        </header>

        {/* Featured Post Section */}
        {featuredPost && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Featured Article</h2>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="md:flex">
                {featuredPost.imageUrl && (
                  <div className="md:flex-shrink-0 md:w-1/3">
                    <img 
                      src={featuredPost.imageUrl} 
                      alt={featuredPost.title} 
                      className="h-64 w-full object-cover md:h-full" 
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-8 md:p-10 md:w-2/3">
                  <div className="uppercase tracking-wide text-sm text-blue-500 font-semibold">
                    {featuredPost.category || 'Featured Post'}
                  </div>
                  <Link 
                    to={`/blog/${featuredPost.slug || featuredPost.id}`} 
                    className="block mt-2 text-2xl font-bold text-gray-900 hover:text-blue-600 transition-colors"
                  >
                    {featuredPost.title}
                  </Link>
                  <p className="mt-2 text-gray-600">
                    {featuredPost.publishDate && (
                      <time dateTime={featuredPost.publishDate.toISOString()}>
                        {format(featuredPost.publishDate, 'MMMM d, yyyy')}
                      </time>
                    )}
                  </p>
                  <p className="mt-4 text-gray-700">
                    {featuredPost.excerpt || featuredPost.content?.substring(0, 150) + '...'}
                  </p>
                  <div className="mt-6">
                    <Link 
                      to={`/blog/${featuredPost.slug || featuredPost.id}`} 
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Read Full Article <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA Banner */}
        <div className="bg-blue-700 text-white rounded-lg shadow-lg p-8 mb-16">
          <div className="md:flex items-center justify-between">
            <div className="mb-4 md:mb-0 md:w-2/3">
              <h2 className="text-2xl font-bold mb-2">Ready to create your video introduction?</h2>
              <p className="text-blue-100">
                Make a lasting impression with a professional video intro for your next job application.
              </p>
            </div>
            <div>
              <a
                href="https://www.reelintro.com/#pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-white text-blue-700 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors"
              >
                Record Your Video Cover Letter Now
              </a>
            </div>
          </div>
        </div>

        {/* Blog Post List */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Latest Articles</h2>
          
          {loading && posts.length === 0 ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No blog posts found. Check back soon!</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map(post => (
                  <BlogPostCard key={post.id} post={post} />
                ))}
              </div>
              
              {hasMore && (
                <div className="text-center mt-12">
                  <button
                    onClick={() => fetchMorePosts()}
                    disabled={loading}
                    className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Loading...' : 'Load More Articles'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default BlogHome;